import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AddRoleModal from "../pages/Settings/AddRoleModal";
import EditRoleModal from "../pages/Settings/EditRoleModal";
import { isAdmin } from "../utils/permissions";

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
function BoolBadge({ value, trueLabel = "Yes", falseLabel = "No", danger = false }) {
  if (value) {
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${danger ? "bg-red-100 text-red-600 ring-1 ring-red-200" : "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${danger ? "bg-red-500" : "bg-emerald-500"}`} />
        {trueLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 ring-1 ring-slate-200">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
      {falseLabel}
    </span>
  );
}

const RefreshIcon = ({ spinning }) => (
  <svg
    className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

function SortIcon({ direction }) {
  if (!direction) {
    return (
      <svg viewBox="0 0 10 14" className="w-2.5 h-3.5 text-slate-300" fill="currentColor">
        <path d="M5 0L9 5H1L5 0z" />
        <path d="M5 14L1 9H9L5 14z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 10 14" className="w-2.5 h-3.5 text-indigo-500" fill="currentColor">
      {direction === "asc"
        ? <path d="M5 0L9 6H1L5 0z" />
        : <path d="M5 14L1 8H9L5 14z" />}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Column config
// ---------------------------------------------------------------------------
const COLUMNS = [
  { key: "rid",         label: "ID",          sortable: true,  width: "w-16"  },
  { key: "name",        label: "Name",         sortable: true,  width: "w-44"  },
  { key: "description", label: "Description",  sortable: false, width: "w-50"  },
  { key: "active",      label: "Active",       sortable: true,  width: "w-24"  },
  { key: "dangerous",   label: "Dangerous",    sortable: true,  width: "w-24"  },
  { key: "action",      label: "Actions",       sortable: false,  width: "w-44"  },
];

// ---------------------------------------------------------------------------
// Sorting helper
// ---------------------------------------------------------------------------
function sortRoles(roles, key, dir) {
  if (!key) return roles;
  return [...roles].sort((a, b) => {
    const av = a[key] ?? "";
    const bv = b[key] ?? "";
    if (typeof av === "boolean") return dir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return dir === "asc" ? cmp : -cmp;
  });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function RolesTable () {
  const [roles, setRoles]     = useState ([]);
  const [addRole, setAddRole] = useState (false);
  const [editingRole, setEditingRole] = useState (null);
  const [loading, setLoading] = useState (true);
  const [error, setError]     = useState (null);
  const [filter, setFilter]   = useState ("");
  const [sortKey, setSortKey] = useState ("rid");
  const [sortDir, setSortDir] = useState ("asc");

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchRoles = useCallback(() => {
    const API_BASE = window.MANTLE_CONFIG.API_URL;
    const token = localStorage.getItem ('jwt');
    
    setLoading(true);
    setError(null);
    axios.get(`${API_BASE}/users/all-roles`, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB 
        },
      })
      .then(({ data }) => setRoles(data))
      .catch((err) => setError(err.response?.data?.message ?? "Failed to load roles."))
      .finally(() => setLoading(false));
  }, []);
  

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // ── Sort toggle ──────────────────────────────────────────────────────────
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────
  const q = filter.toLowerCase();
  const filtered = roles.filter(
    (r) =>
      String(r.id).includes(q) ||
      r.name?.toLowerCase().includes(q) ||
      r.description?.toLowerCase().includes(q)
  );
  const sorted = sortRoles(filtered, sortKey, sortDir);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">

        {/* ── Page header ── */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500 mt-0.5">
              {loading ? "Loading …" : `${sorted.length} role${sorted.length !== 1 ? "s" : ""}${filter ? " matched" : ""}`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <svg viewBox="0 0 16 16" className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" fill="none">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                placeholder="Filter roles …"
                value={filter}
                onChange={(e) => setFilter (e.target.value)}
                className="pl-8 pr-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition w-52"
              />
            </div>
            {/* Refresh */}
            <button
              onClick={fetchRoles}
              disabled={loading}
              aria-label="Refresh"
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:text-slate-700 hover:border-slate-300 transition disabled:opacity-40"
            >
              <RefreshIcon spinning={loading} />
            </button>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <svg viewBox="0 0 16 16" className="w-4 h-4 flex-shrink-0" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 5v3.5M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {error}
            <button onClick={fetchRoles} className="ml-auto text-xs font-medium underline hover:no-underline">
              Retry
            </button>
          </div>
        )}

        {/* ── Table card ── */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">

              {/* ── Head ── */}
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider ${col.width} ${col.sortable ? "cursor-pointer select-none hover:text-slate-700 transition-colors" : ""}`}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {col.label}
                        {col.sortable && (
                          <SortIcon direction={sortKey === col.key ? sortDir : null} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* ── Body ── */}
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {COLUMNS.map((col) => (
                        <td key={col.key} className="px-4 py-3">
                          <div className="h-4 bg-slate-100 rounded w-3/4" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-slate-400 text-sm">
                      {filter ? "No roles match your filter." : "No roles found."}
                    </td>
                  </tr>
                ) : (
                  sorted.map((role) => (
                    <tr key={role.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-700/40 transition-colors">

                      {/* ID */}
                      <td className="px-4 py-3 font-mono text-xs text-slate-400 tabular-nums" >
                        #{role.id}
                      </td>

                      {/* Name */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 dark:text-slate-100">{role.name}</div>
                        <div className="text-xs text-slate-400 dark:text-slate-500 font-mono">{role.code}</div>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-300 max-w-sm">
                        <span className="line-clamp-2">{role.description || "—"}</span>
                      </td>
                      
                      {/* Active */}
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-300 max-w-sm">
                        <BoolBadge value={role.active} trueLabel="Yes" falseLabel="No" danger={false} />
                      </td>

                      {/* Dangerous */}
                      <td className="px-4 py-3">
                        <BoolBadge value={role.dangerous} trueLabel="Yes" falseLabel="No" danger={true} />
                      </td>

                      {/* Action Buttons */}
                      <td className="px-4 py-3">
                        <button onClick={() => setEditingRole(role)}
                        className="text-xs text-slate-500 border border-slate-200 rounded px-2.5 py-1 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed hover:text-slate-700 transition-colors font-medium"
                        disabled={!isAdmin() || role.locked}
                        >Edit/Update
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Footer ── */}
          {!loading && sorted.length > 0 && (
            <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between text-xs text-slate-400">
              <span>{sorted.length} of {roles.length} roles</span>
              {filter && (
                <button
                  onClick={() => setFilter("")}
                  className="text-indigo-500 hover:text-indigo-700 transition-colors font-medium"
                >
                  Clear filter
                </button>
              )}
              {/* Add Role */}
             <button
                  onClick={() => setAddRole(true)}
                  className="text-green-500 hover:text-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isAdmin()}
                >
                  Add Role
                </button>
            </div>
          )}
          
          {editingRole && (
            <EditRoleModal
              role={editingRole}   // the role object from your data source
              onClose={() => setEditingRole(null)}
              onSuccess={(updated) => fetchRoles()}
            />
          )}
          
          <AddRoleModal
            isOpen={addRole}
            onClose={() => setAddRole(false)}
            onSuccess={(newRole) => fetchRoles()}
          />
        </div>

      </div>
    </div>
  );
}
